import React, { createContext, useContext, useState, useCallback, useMemo, FC, PropsWithChildren, useEffect } from 'react';
import { BookingData, BookingContextType } from './bookingTypes';

const STORAGE_KEY = 'booking-data';

const BookingContext = createContext<BookingContextType | null>(null);

export const BookingProvider: FC<PropsWithChildren> = ({ children }) => {
  const [bookingData, setBookingData] = useState<BookingData | null>(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored ? (JSON.parse(stored) as BookingData) : null;
    } catch {
      return null;
    }
  });

  // Maintain booking state between refreshes
  useEffect(() => {
    if (bookingData) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(bookingData));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [bookingData]);

  const setBookingContext = useCallback((data: BookingData) => {
    setBookingData(data);
  }, []);

  const patchBookingContext = useCallback(
    (data: Partial<BookingData>) =>
      setBookingData((previousState) => {
        if (!previousState) return null;

        return {
          ...previousState,
          ...data
        };
      }),
    []
  );

  const providerValue: BookingContextType = useMemo(
    () => ({
      bookingData,
      setBookingContext,
      patchBookingContext
    }),
    [bookingData, patchBookingContext, setBookingContext]
  );

  return <BookingContext.Provider value={providerValue}>{children}</BookingContext.Provider>;
};

export const useBookingContext = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBookingContext must be used inside BookingProvider');
  }
  return context;
};
